import React, { useState } from 'react';
import { Image } from 'react-bootstrap';
import { Building, AlertCircle } from 'lucide-react';

/**
 * Composant pour afficher les logos de boutiques - Basé sur ProductImage.js
 */
const BoutiqueLogo = ({ 
  boutique, 
  size = 'medium', 
  className = '', 
  style = {},
  showPlaceholder = true,
  alt = null
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const fixLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;
    
    
    // Si l'URL commence par /api/uploads/, ajouter le serveur backend
    if (logoUrl.startsWith('/api/uploads/')) {
      const correctedUrl = `${process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/smartboutik'}${logoUrl}`;
      return correctedUrl;
    }
    
    // Si l'URL pointe vers le serveur React par erreur, corriger
    if (logoUrl.includes('localhost:3000/api/uploads/')) {
      const correctedUrl = logoUrl.replace('localhost:3000/', 'localhost:8082/');
      return correctedUrl;
    }
    
    // Si c'est déjà une URL complète vers le serveur de production
    if (logoUrl.startsWith('https://apps.prod.atlanticds-sn.com/smartboutik/api/uploads/')) {
      return logoUrl;
    }
    
    // Si c'est déjà une URL complète vers le serveur local
    if (logoUrl.startsWith('https://apps.prod.atlanticds-sn.com/smartboutik/api/uploads/')) {
      return logoUrl;
    }
    
    // Si c'est juste un nom de fichier ou chemin relatif
    if (!logoUrl.startsWith('http')) {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/smartboutik';
      const correctedUrl = `${baseUrl}/api/uploads/${logoUrl}`;
      return correctedUrl;
    }
    
    return logoUrl;
  };
  
  /**
   * Obtenir l'URL du logo de la boutique (même logique que getImageUrl)
   */
  const getLogoUrl = () => {
    if (!boutique) return null;
    
    // Essayer différentes propriétés de logo (comme pour les produits)
    const rawLogoUrl = boutique.logoUrl || boutique.logo_url || boutique.logo;
    
    if (!rawLogoUrl) {
      return null;
    }
    
    return fixLogoUrl(rawLogoUrl);
  };

  /**
   * Dimensions selon la taille demandée (même logique que ProductImage)
   */
  const getDimensions = () => {
    switch (size) {
      case 'small':
        return { width: 50, height: 50 };
      case 'medium':
        return { width: 80, height: 80 };
      case 'large':
        return { width: 120, height: 120 };
      case 'xl':
        return { width: 200, height: 200 };
      default:
        return { width: 80, height: 80 };
    }
  };

  /**
   * Générer les initiales de la boutique pour le fallback
   */
  const getBoutiqueInitials = () => {
    if (!boutique?.name) return 'B';
    
    const words = boutique.name.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    
    return words
      .slice(0, 2)
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  };

  const logoUrl = getLogoUrl();
  const dimensions = getDimensions();
  const altText = alt || `Logo de ${boutique?.name || 'boutique'}`;

  // Style de base pour les logos (même que ProductImage)
  const baseStyle = {
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid #dee2e6',
    backgroundColor: '#f8f9fa',
    ...dimensions,
    ...style
  };

  // Si aucun logo ou erreur de chargement (même logique que ProductImage)
  if (!logoUrl || imageError) {
    if (!showPlaceholder) return null;
    
    return (
      <div 
        className={`d-flex align-items-center justify-content-center ${className}`}
        style={{
          ...baseStyle,
          backgroundColor: boutique?.primaryColor || '#3b82f6',
          color: '#ffffff',
          fontWeight: '700',
          fontSize: `${dimensions.width * 0.35}px`,
          border: '2px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
        title={`${imageError ? 'Erreur de chargement' : 'Aucun logo'} pour ${boutique?.name || 'cette boutique'}`}
      >
        {imageError ? (
          <AlertCircle size={dimensions.width * 0.3} />
        ) : (
          getBoutiqueInitials()
        )}
      </div>
    );
  }

  return (
    <div className={`position-relative ${className}`}>
      {/* Logo principal (même structure que ProductImage) */}
      <Image
        src={logoUrl}
        alt={altText}
        style={baseStyle}
        onLoad={() => {
          setImageLoading(false);
          setImageError(false);
        }}
        onError={(e) => {
          setImageLoading(false);
          setImageError(true);
          console.error('❌ Erreur chargement logo:', logoUrl);
          console.error('❌ Détails erreur:', e);
        }}
      />
      
      {/* Indicateur de chargement (même que ProductImage) */}
      {imageLoading && (
        <div 
          className="position-absolute top-0 start-0 d-flex align-items-center justify-content-center"
          style={{
            ...dimensions,
            backgroundColor: 'rgba(248, 249, 250, 0.8)',
            borderRadius: '8px'
          }}
        >
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Composants simplifiés (même pattern que ProductImage)
 */
const BoutiqueLogoSmall = ({ boutique, className = '' }) => (
  <BoutiqueLogo 
    boutique={boutique} 
    size="small" 
    className={className}
  />
);

const BoutiqueLogoLarge = ({ boutique, className = '' }) => (
  <BoutiqueLogo 
    boutique={boutique} 
    size="large" 
    className={className}
  />
);

export const generateBoutiqueLogoHTML = (boutique, options = {}) => {
  const {
    width = 60,
    height = 40,
    showInitials = true
  } = options;

  // Utiliser la même logique que le composant
  const fixLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;
    
    if (logoUrl.startsWith('/api/uploads/')) {
      return `${process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/smartboutik'}${logoUrl}`;
    }
    
    if (logoUrl.includes('localhost:3000/api/uploads/')) {
      return logoUrl.replace('localhost:3000/', 'https://apps.prod.atlanticds-sn.com/smartboutik/');
    }
    
    if (logoUrl.startsWith('http')) {
      return logoUrl;
    }
    
    const baseUrl = process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/smartboutik';
    return `${baseUrl}/api/uploads/${logoUrl}`;
  };

  const getBoutiqueInitials = () => {
    if (!boutique?.name) return 'B';
    
    const words = boutique.name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    
    return words
      .slice(0, 2)
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  };

  const logoUrl = fixLogoUrl(boutique?.logoUrl || boutique?.logo);
  const initials = getBoutiqueInitials();

  // Si un logo existe
  if (logoUrl) {
    return `
      <div class="logo-container" style="text-align: center; margin-bottom: 6px;">
        <img 
          src="${logoUrl}" 
          alt="Logo ${boutique?.name || 'Boutique'}"
          style="max-width: ${width}px; max-height: ${height}px; object-fit: contain; border-radius: 4px;"
          onerror="this.style.display='none'; this.nextElementSibling.style.display='block';"
        />
        <div style="display: none; width: 40px; height: 40px; background: #333; color: white; border-radius: 6px; margin: 0 auto; line-height: 40px; font-weight: bold; font-size: 14px;">
          ${initials}
        </div>
      </div>
    `;
  }

  // Fallback avec initiales
  if (showInitials) {
    return `
      <div class="logo-container" style="text-align: center; margin-bottom: 6px;">
        <div style="width: 40px; height: 40px; background: ${boutique?.primaryColor || '#333'}; color: white; border-radius: 6px; margin: 0 auto; line-height: 40px; font-weight: bold; font-size: 14px; font-family: Arial, sans-serif;">
          ${initials}
        </div>
      </div>
    `;
  }

  // Fallback simple
  return `
    <div class="logo-container" style="text-align: center; margin-bottom: 6px; font-size: 24px;">
      🏪
    </div>
  `;
};

export default BoutiqueLogo;
export { BoutiqueLogoSmall, BoutiqueLogoLarge };